import React, { useState, useEffect, useContext } from "react";
import { getFavoriteEvents, addFavoriteEvents } from "../../../api/favoriteEvents";
import { getSelectedEvents } from "../../../api/events";
import { UserContext } from "../../../api/userContext";
import { getDefaultImage } from "../../../utils/eventUtils";
import { sortEventsByCategoryPriority } from "../../../utils/eventCategoryOrder";
import { formatDateOnly, formatEventRecurrenceLabel, isSameCalendarDay } from "../../../utils/dateUtils";
import { truncateAtLastSpace } from "../../../utils/textUtils";
import EventCardSkeleton from "../home/EventCardSkeleton";
import NoEventsState from "../home/NoEventsState";
import "../../../assets/css/animate.css";
import "./events.css";

const Events = () => {
  const { user } = useContext(UserContext);
  const [events, setEvents] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const serverUrl = process.env.REACT_APP_SERVER_URL;

  const imageStyle = {
    width: '100%',
    height: 'auto',
    maxHeight: '200px',
    objectFit: 'cover'
  };

  const getBackgroundColor = (index) => {
    const colors = [
      'rgba(47, 172, 102, 0.6)',
      'rgba(206, 20, 70, 0.6)',
      'rgba(47, 137, 252, 0.6)',
      'rgba(255, 153, 0, 0.6)',
      'rgba(156, 39, 176, 0.6)',
      'rgba(0, 150, 136, 0.6)',
      'rgba(233, 30, 99, 0.6)',
      'rgba(255, 193, 7, 0.6)',
      'rgba(103, 58, 183, 0.6)',
      'rgba(96, 125, 139, 0.6)'
    ];
    return colors[index % 10];
  };

  const getLightBackgroundColor = (index) => {
    const colors = [
      'rgba(47, 172, 102, 0.2)',
      'rgba(206, 20, 70, 0.2)',
      'rgba(47, 137, 252, 0.2)',
      'rgba(255, 153, 0, 0.2)',
      'rgba(156, 39, 176, 0.2)',
      'rgba(0, 150, 136, 0.2)',
      'rgba(233, 30, 99, 0.2)',
      'rgba(255, 193, 7, 0.2)',
      'rgba(103, 58, 183, 0.2)',
      'rgba(96, 125, 139, 0.2)'
    ];
    return colors[index % 10];
  };

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const navigateToDetails = (id) => {
    scrollTop();
    window.location.href = `${process.env.PUBLIC_URL}/event-details?eid=${id}`;
  };

  useEffect(() => {
    const fetchFavoriteEvents = async () => {
      if (user) {
        try {
          const favorites = await getFavoriteEvents(user.uid);
          const favoriteEvents = await getSelectedEvents(favorites.map(event => event.eventId));
          const orderedEvents = sortEventsByCategoryPriority(favoriteEvents);
          console.log('Favorite events:', orderedEvents);
          setEvents(orderedEvents);
          setFavorites(favorites);
        } catch (error) {
          console.error('Failed to load favorite events:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchFavoriteEvents();
  }, [user]);

  const handleFavoriteClick = async (eventId) => {
    let updatedFavorites;
    try {
      if (favorites.map(fav => fav.eventId).includes(eventId)) {
        updatedFavorites = favorites.filter(event => event.eventId !== eventId);
      } else {
        updatedFavorites = [...favorites, { eventId, favoritedAt: Date.now() }];
      }
      setFavorites(updatedFavorites);

      if (!user) {
        return;
      }
    
      const success = await addFavoriteEvents(user.uid, updatedFavorites);
      if (!success) {
        throw new Error('Failed to update favorite');
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
      return;
    }
  };

  return (
    <>
      {/* ===============  Event Area start  =============== */}
      <div className="event-area">
        <div className="container position-relative pt-110">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-head">
                <h3>évènements favoris</h3>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="all-event-cards">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-event1"
                    role="tabpanel"
                    aria-labelledby="pills-tab1"
                  >
                    <div className="row">
                      {loading ? (
                        // Skeleton loading cards
                        [...Array(6)].map((_, index) => (
                          <EventCardSkeleton key={`skeleton-${index}`} />
                        ))
                      ) : events.length > 0 ? (
                        events.map((event, index) => {
                          const recurrenceLabel = formatEventRecurrenceLabel(event);
                          return (
                          <div
                            key={`${event.id}-${index}`}
                            className="col-lg-4 col-md-6 wow fadeInUp animated"
                            data-wow-delay="200ms"
                            data-wow-duration="1500ms"
                          >
                            <div 
                              className="event-card-md event-card-clickable"
                              onClick={(e) => {
                                if (e.target.closest('.event-share-icons') || 
                                    e.target.closest('.favorite-icon') ||
                                    e.target.closest('a[href^="https://"]')) {
                                  return;
                                }
                                navigateToDetails(event.id);
                              }}
                            >
                              <div className="event-thumb" style={{ position: 'relative' }}>
                                {/* Floating Date Badge */}
                                <div className="event-date-badge">
                                  <span className="date-day">{new Date(event.date).getDate()}</span>
                                  <span className="date-month">{new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                                  {event.endDate && !isSameCalendarDay(event.date, event.endDate) && (
                                    <>
                                      <span className="date-separator">→</span>
                                      <span className="date-day">{new Date(event.endDate).getDate()}</span>
                                      <span className="date-month">{new Date(event.endDate).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                                    </>
                                  )}
                                </div>

                                {/* Image */}
                                <img
                                  src={getDefaultImage(event)}
                                  alt={event.title || 'Event'}
                                  style={imageStyle}
                                />

                                {/* Overlay */}
                                <div
                                  className="event-overlay"
                                  style={{ background: getLightBackgroundColor(index), zIndex: 2 }}
                                />

                                <div className="event-info-container">
                                  <div
                                    className="event-lavel"
                                    style={{ background: getBackgroundColor(index), zIndex: 3 }}
                                  >
                                    <i className="bi bi-diagram-3" />
                                    <span>{event.category}</span>
                                  </div>
                                  {user && (
                                    <div
                                      className="event-favorite"
                                      style={{ background: getBackgroundColor(index), zIndex: 3 }}
                                    >
                                      <span
                                        style={{ cursor: 'pointer', zIndex: 3 }}
                                        className="favorite-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFavoriteClick(event.id);
                                        }}
                                      >
                                        <i className={favorites.map(fav => fav.eventId).includes(event.id) ? "bi bi-heart-fill" : "bi bi-heart"} />
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Title block */}
                                <div
                                  className="event-title"
                                  style={{ background: getBackgroundColor(index) }}
                                >
                                  <h4>{event.title.length > 100 ? truncateAtLastSpace(event.title, 100) : event.title}</h4>
                                </div>
                              </div>
                              <div className="event-content">
                                <div className="event-info">
                                  <div className="event-date">
                                    <span className="event-date-text">
                                      <i className="bi bi-calendar2-week" />{" "}
                                      {event.endDate
                                        ? (
                                          isSameCalendarDay(event.date, event.endDate)
                                            ? formatDateOnly(event.date)
                                            : <>
                                              {`Du ${formatDateOnly(event.date)}`}
                                              <br />
                                              <span className="endDate">{`Au ${formatDateOnly(event.endDate)}`}</span>
                                            </>
                                        )
                                        : formatDateOnly(event.date)
                                      }
                                      {recurrenceLabel && (
                                        <>
                                          <br />
                                          <span className="endDate">{recurrenceLabel}</span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                  <div className="event-location">
                                    <span className="event-location-text">
                                      <i className="bi bi-geo-alt" />
                                      <>
                                        {event.location.cityName}<br />
                                        <span className="postal-code">{event.location.postalCode && `${event.location.postalCode}`}</span>
                                      </>
                                    </span>
                                  </div>
                                </div>
                                <h6 className="event-speaker">
                                  <p>{event.speaker ? `Intervenant : ` : ""}<span>{event.speaker || "\u00A0"}</span></p>
                                </h6>
                                <h3 className="event-organizedby">
                                  <p>{event.organizer ? `organisé par : ` : ""}<span>{event.organizer || "\u00A0"}</span></p>
                                </h3>
                                <div className="event-bottom">
                                  <div className="event-readme">
                                    <span className="event-details-link">
                                      Détails <i className="bi bi-arrow-right"></i>
                                    </span>
                                  </div>
                                  <div className="event-share-icons" onClick={(e) => e.stopPropagation()}>
                                    <ul className="share-options">
                                      <li>
                                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${serverUrl}/event-details?eid=${event.id}`} target="_blank" rel="noreferrer">
                                          <i className="fab fa-facebook-f" />
                                        </a>
                                      </li>
                                      <li>
                                        <a href={`https://www.instagram.com/?url=${serverUrl}/event-details?eid=${event.id}`} target="_blank" rel="noreferrer">
                                          <i className="fab fa-instagram" />
                                        </a>
                                      </li>
                                      <li>
                                        <a href={`https://twitter.com/intent/tweet?url=${serverUrl}/event-details?eid=${event.id}&text=${event.title}`} target="_blank" rel="noreferrer">
                                          <i className="fa-brands fa-x-twitter" />
                                        </a>
                                      </li>
                                    </ul>
                                    <div className="share-btn">
                                      <i className="bi bi-share" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })
                      ) : (
                        <div className="col-lg-12">
                          <NoEventsState type="favorites" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="custom-pagination text-center">
                <ul className="page-list">
                  <li>
                    <Link
                      onClick={() => loadMoreEvents('prev')}
                      to={"#"}
                      disabled={!firstVisible || loading}
                    >
                      <i className="bi bi-arrow-left"></i>
                    </Link>
                  </li>
                  <li>
                    <Link
                      onClick={() => loadMoreEvents('next')}
                      to={"#"}
                      disabled={!lastVisible || loading}
                    >
                      <i className="bi bi-arrow-right"></i>
                    </Link>
                  </li>
                </ul>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/* ===============  Event Area end  =============== */}
    </>
  );
};

export default Events;
